import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar, MapPin, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/guest-portal`;

const GuestPortal = () => {
  const [step, setStep] = useState('login'); // login, dashboard
  const [email, setEmail] = useState('');
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [token, setToken] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/login`, {
        email,
        confirmation_number: confirmationNumber
      });
      setToken(response.data.access_token);
      await fetchReservations(response.data.access_token);
      setStep('dashboard');
      toast.success('Login successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const fetchReservations = async (authToken) => {
    try {
      const response = await axios.get(`${API}/my-reservations?token=${authToken}`);
      setReservations(response.data);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    }
  };

  const handleDigitalCheckIn = async (reservationId) => {
    const arrival = prompt('Estimated arrival time (HH:MM):');
    if (!arrival) return;

    try {
      await axios.post(
        `${API}/digital-checkin/${reservationId}?token=${token}`,
        {
          estimated_arrival_time: arrival,
          special_requests: '',
          vehicle_info: ''
        }
      );
      toast.success('Check-in completed!');
      fetchReservations(token);
    } catch (error) {
      toast.error('Check-in failed');
    }
  };

  if (step === 'login') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-50 via-blue-50 to-slate-50 p-6">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Guest Portal</CardTitle>
            <p className="text-slate-600 text-center">Access your reservation</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="guest@email.com"
                  required
                />
              </div>
              <div>
                <Label>Confirmation Number</Label>
                <Input
                  value={confirmationNumber}
                  onChange={(e) => setConfirmationNumber(e.target.value)}
                  placeholder="ABC12345"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Logging in...' : 'Access My Reservation'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-slate-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900">My Reservations</h1>
          <p className="text-slate-600">Welcome back!</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reservations.map((res) => (
            <Card key={res.id} className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin size={20} className="text-cyan-600" />
                  {res.property?.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-slate-600">
                  <Calendar size={16} />
                  <span>{format(new Date(res.check_in), 'MMM dd, yyyy')} - {format(new Date(res.check_out), 'MMM dd, yyyy')}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <User size={16} />
                  <span>{res.adults} Adults, {res.children} Children</span>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Confirmation</p>
                  <p className="font-mono font-bold text-lg">{res.id.substr(0, 8).toUpperCase()}</p>
                </div>
                <div className="flex items-center justify-between pt-4 border-t">
                  <div>
                    <p className="text-sm text-slate-600">Status</p>
                    <p className="font-semibold text-cyan-600">{res.status}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-slate-900">${res.base_amount + res.tax_amount}</p>
                  </div>
                </div>
                {res.status === 'CONFIRMED' && !res.digital_checkin_completed && (
                  <Button
                    onClick={() => handleDigitalCheckIn(res.id)}
                    className="w-full"
                  >
                    Complete Digital Check-in
                  </Button>
                )}
                {res.digital_checkin_completed && (
                  <div className="text-center py-2 bg-emerald-50 rounded-lg text-emerald-700">
                    ✓ Digital check-in completed
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GuestPortal;
